import type * as PI from '@sosocio/creator-types/project';
import { createActor } from 'xstate';
import { photoProcessingQueueMachine } from './machines';

function asError(value: unknown): Error {
	return value instanceof Error ? value : new Error(String(value));
}

function throwIfAbortSignalAborted(abortSignal: AbortSignal): void {
	if (abortSignal.aborted) {
		throw new Error('Photo processing aborted');
	}
}

export type PhotoQueueInput = OptionalExceptFor<PI.PhotoModel, 'id'>;
export type PhotoQueueCallback = (error: Error | null, photoModel: PI.PhotoModel) => void;

type PhotoQueueTask = {
	attempts: number;
	callback?: PhotoQueueCallback;
	id: string;
	input: PhotoQueueInput;
};

export type PhotoProcessingQueueDependencies = {
	addPhotos: (ids: number[]) => void;
	changePhotoId: (ids: [string, number]) => Promise<void>;
	clearTimeout: (timeoutId: number) => void;
	createPhotoModel: (input: PhotoQueueInput, abortSignal: AbortSignal) => Promise<PI.PhotoModel>;
	getPhotoById: (id: PI.PhotoModel['id']) => PI.PhotoModel | undefined;
	hasSavedPhoto: (id: string) => boolean;
	isOnline: () => boolean;
	removePhoto: (id: string) => void;
	setTimeout: (handler: () => void, timeout: number) => number;
	trackPhotoProcessed: (photoModel: PI.PhotoModel, startTime: number) => void;
	updatePhotoModel: (data: OptionalExceptFor<PI.PhotoModel, 'id'>) => void;
};

export type PhotoProcessingQueueOptions = {
	concurrentTasks: number;
	/** Number of retries after the initial attempt. */
	maxRetries: number;
	maxTaskTime: number;
	offlineRetryDelay: number;
	retryDelay: number;
};

const defaultOptions: PhotoProcessingQueueOptions = {
	concurrentTasks: 2,
	maxRetries: 3,
	maxTaskTime: 60 * 1000,
	offlineRetryDelay: 5 * 1000,
	retryDelay: 1000,
};

function isRetryablePhotoError(err: Error): boolean {
	/*
	 * Callers normalize thrown values into an Error before reaching this
	 * decision (see asError), so a thrown primitive behaves deterministically:
	 * a plain Error has no `retryable` flag and is therefore treated as
	 * retryable. Only an explicit `retryable: false` marks a permanent failure.
	 */
	return (err as PI.PhotoProcessingError).retryable !== false;
}

export class PhotoProcessingQueue {
	private tasksById = new Map<string, PhotoQueueTask>();

	private offlineTimer: number | null = null;

	/**
	 * Every pending maxTaskTime / retryDelay handle. Tracked so {@link destroy}
	 * can clear them; otherwise a fired retry timer could re-enqueue a task for
	 * an already torn-down queue (ghost re-enqueue).
	 */
	private pendingTimers = new Set<number>();

	private actor: ReturnType<typeof createActor<typeof photoProcessingQueueMachine>>;

	public constructor(
		private readonly dependencies: PhotoProcessingQueueDependencies,
		private readonly options: PhotoProcessingQueueOptions = defaultOptions,
	) {
		this.actor = createActor(
			photoProcessingQueueMachine,
			{
				input: {
					maxActive: options.concurrentTasks,
					online: true,
				},
			},
		);
		this.actor.on(
			'photoQueue.task.activated',
			(event) => {
				this.onTaskActivated(event.id);
			},
		);
		this.actor.start();
	}

	/**
	 * Schedules a timer through the injected setTimeout, tracking its handle so
	 * {@link destroy} can cancel it and untracking it once it fires.
	 */
	private scheduleTimer(
		handler: () => void,
		timeout: number,
	): number {
		const timerId = this.dependencies.setTimeout(
			() => {
				this.pendingTimers.delete(timerId);
				handler();
			},
			timeout,
		);
		this.pendingTimers.add(timerId);

		return timerId;
	}

	private clearTimer(timerId: number): void {
		this.pendingTimers.delete(timerId);
		this.dependencies.clearTimeout(timerId);
	}

	/**
	 * Tears the queue down: cancels every pending timer, drops queued tasks, and
	 * stops the actor so no late timer can re-enqueue ghost work.
	 */
	public destroy(): void {
		this.pendingTimers.forEach((timerId) => {
			this.dependencies.clearTimeout(timerId);
		});
		this.pendingTimers.clear();
		this.offlineTimer = null;
		this.tasksById.clear();
		this.actor.stop();
	}

	private onTaskActivated(id: string): void {
		const task = this.tasksById.get(id);

		if (!task) {
			/*
			 * The machine activated an id we have no task for. This should never
			 * happen and points at a bookkeeping bug, but if we just returned the
			 * id would stay in the machine's activeIds forever and permanently
			 * burn a concurrency slot. Reclaim the slot and surface the anomaly.
			 */
			console.warn(`PhotoProcessingQueue: activated unknown task id "${id}", reclaiming slot`);
			this.actor.send({
				type: 'photoQueue.task.removed',
				id,
			});

			return;
		}

		this.runTask(task);
	}

	public push(
		input: PhotoQueueInput,
		callback?: PhotoQueueCallback,
	): void {
		if (!input.full_url) {
			const error = new Error('Missing photo url');
			this.failInput(
				input,
				error,
			);
			callback?.(
				error,
				input as PI.PhotoModel,
			);
			return;
		}

		const taskId = String(input.id);
		this.tasksById.set(
			taskId,
			{
				attempts: 0,
				callback,
				id: taskId,
				input,
			},
		);
		this.dependencies.updatePhotoModel({
			id: input.id,
			_processing: true,
		});
		const online = this.syncOnlineState();
		this.actor.send({
			type: 'photoQueue.task.accepted',
			id: taskId,
		});

		if (!online) {
			this.scheduleOfflinePoll();
		}
	}

	private syncOnlineState(): boolean {
		const online = this.dependencies.isOnline();
		this.actor.send({
			type: online ? 'network.online' : 'network.offline',
		});

		return online;
	}

	private scheduleOfflinePoll(): void {
		if (this.offlineTimer !== null) {
			return;
		}

		/*
		 * The queue is a long-lived singleton; if nothing is queued there is
		 * nothing for an online transition to drive forward, so the 5s poll
		 * just burns wakeups. Subsequent push() / runTask() calls reschedule
		 * the poll when work appears.
		 */
		if (this.tasksById.size === 0) {
			return;
		}

		this.offlineTimer = this.scheduleTimer(
			() => {
				this.offlineTimer = null;
				const online = this.syncOnlineState();

				if (!online) {
					this.scheduleOfflinePoll();
				}
			},
			this.options.offlineRetryDelay,
		);
	}

	/**
	 * Process one attempt, allowing the backend's longer multipage PDF execution
	 * window only when the request explicitly asks for multiple design pages.
	 *
	 * @param task Queued input and retry bookkeeping for this attempt.
	 */
	private runTask(task: PhotoQueueTask): void {
		/*
		 * Pre-increment: attempts counts the run about to start, so the first run
		 * sets attempts to 1. handleTaskFailure then retries while
		 * attempts <= maxRetries, giving exactly maxRetries retries after the
		 * initial attempt (1 initial + maxRetries retries).
		 */
		task.attempts += 1;

		let settled = false;
		const abortController = new AbortController();
		const timeoutId = this.scheduleTimer(
			() => {
				if (settled) {
					return;
				}

				settled = true;
				abortController.abort();
				this.handleTaskFailure(
					task,
					new Error('Photo processing timed out'),
				);
			},
			task.input._vectorize?.isDesignPrint && (task.input._vectorize.designPrintMaxPages ?? 1) > 1
				? Math.max(
					this.options.maxTaskTime,
					330 * 1000,
				)
				: this.options.maxTaskTime,
		);

		void this.processInput(
			task.input,
			abortController.signal,
		)
			.then((photoModel) => {
				if (settled) {
					return;
				}

				settled = true;
				this.clearTimer(timeoutId);
				this.handleTaskSuccess(
					task,
					photoModel,
				);
			})
			.catch((err: unknown) => {
				if (settled) {
					return;
				}

				settled = true;
				this.clearTimer(timeoutId);
				this.handleTaskFailure(
					task,
					asError(err),
				);
			});
	}

	/**
	 * Save every processed output before resolving the primary upload. Register
	 * additional design pages only while the user still has the original selected.
	 *
	 * @param input Local photo to persist or an already saved photo to reuse.
	 * @param abortSignal Cancellation signal shared with the backend request.
	 * @returns The primary saved {@link PI.PhotoModel} after project registration.
	 */
	private async processInput(
		input: PhotoQueueInput,
		abortSignal: AbortSignal,
	): Promise<PI.PhotoModel> {
		throwIfAbortSignalAborted(abortSignal);

		if (typeof input.id === 'number') {
			/*
			 * Sometimes an already saved photo enters the queue.
			 * In that case we can use the existing model without saving it again.
			 */
			const photoModel = this.dependencies.getPhotoById(input.id);

			if (!photoModel) {
				throw new Error(`Photo with id ${input.id} not found`);
			}

			return photoModel;
		}

		const oldId = input.id;
		const startTime = new Date().getTime();

		/*
		 * Failures (retryable or not) propagate to runTask's catch and are
		 * resolved by handleTaskFailure. A non-retryable error must NOT be
		 * swallowed here and returned as a resolved photo model: that would
		 * report a permanent failure to the callback as success with a null
		 * error. handleTaskFailure's terminal path already records _error /
		 * _processing:false, releases the slot, and reports the error.
		 */
		const photoModel = await this.dependencies.createPhotoModel(
			input,
			abortSignal,
		);
		throwIfAbortSignalAborted(abortSignal);

		this.dependencies.trackPhotoProcessed(
			photoModel,
			startTime,
		);

		const newId = photoModel.id;

		if (typeof newId !== 'number') {
			throw new Error('Created photo model did not receive a remote id');
		}

		/*
		 * Check if product is still in product photo selection.
		 * The user can have deselected the photo while processing on the server.
		 */
		if (this.dependencies.hasSavedPhoto(oldId)) {
			throwIfAbortSignalAborted(abortSignal);
			const additionalPhotoIds: number[] = photoModel._designPrint?.outputPhotoIds?.slice(1) ?? [];

			if (additionalPhotoIds.length > 0) {
				this.dependencies.addPhotos(additionalPhotoIds);
			}

			await this.dependencies.changePhotoId([
				oldId,
				newId,
			]);

			return photoModel;
		}

		throwIfAbortSignalAborted(abortSignal);
		this.dependencies.removePhoto(oldId);
		return photoModel;
	}

	private handleTaskSuccess(
		task: PhotoQueueTask,
		photoModel: PI.PhotoModel,
	): void {
		const taskId = task.id;
		this.tasksById.delete(taskId);
		this.releaseTask(taskId);
		this.dependencies.updatePhotoModel({
			id: photoModel.id,
			_processing: false,
		});
		task.callback?.(
			null,
			photoModel,
		);
	}

	private releaseTask(taskId: string): void {
		const online = this.syncOnlineState();

		if (!online) {
			this.scheduleOfflinePoll();
		}

		this.actor.send({
			type: 'photoQueue.task.released',
			id: taskId,
		});
	}

	private handleTaskFailure(
		task: PhotoQueueTask,
		error: Error,
	): void {
		const taskId = task.id;

		/*
		 * attempts is pre-incremented in runTask, so this allows exactly
		 * maxRetries retries after the initial attempt.
		 */
		if (
			isRetryablePhotoError(error)
			&& task.attempts <= this.options.maxRetries
		) {
			this.releaseTask(taskId);
			this.scheduleTimer(
				() => {
					/*
					 * Guard against re-enqueuing a task that was torn down (e.g.
					 * destroy() cleared tasksById) while the retry timer was
					 * pending, which would otherwise resurrect ghost work.
					 */
					if (!this.tasksById.has(taskId)) {
						return;
					}

					const online = this.syncOnlineState();
					this.actor.send({
						type: 'photoQueue.task.accepted',
						id: taskId,
					});

					if (!online) {
						this.scheduleOfflinePoll();
					}
				},
				this.options.retryDelay,
			);
			return;
		}

		this.tasksById.delete(taskId);
		this.releaseTask(taskId);
		this.failInput(
			task.input,
			error,
		);
		task.callback?.(
			error,
			task.input as PI.PhotoModel,
		);
	}

	private failInput(
		input: PhotoQueueInput,
		error: Error,
	): void {
		this.dependencies.updatePhotoModel({
			id: input.id,
			_error: error,
			_processing: false,
		});
	}
}
